import { Popover, Typography } from "@mui/material";
import { ToggleButton } from "@verity/components/common";
import { useAtom, useAtomValue } from "jotai";
import { atomWithStorage } from "jotai/utils";
import React from "react";
import { useTranslation } from "react-i18next";
import styles from "./DetectionOverlaySettings.module.css";
import GearIcon from "./Gear.svg?react";

export const faceDetectionEnabledAtom = atomWithStorage("faceDetectionEnabled", true);
export const objectDetectionEnabledAtom = atomWithStorage("objectDetectionEnabled", true);
export const gestureDetectionEnabledAtom = atomWithStorage("gestureDetectionEnabled", false);
export const gazeDirectionEnabledAtom = atomWithStorage("gazeDirectionEnabled", false);
export const faceDetectionCapabilityAtom = atomWithStorage("faceDetectionCapability", false);
export const objectDetectionCapabilityAtom = atomWithStorage("objectDetectionCapability", false);
export { GearIcon };

interface DetectionSettingsProps {
	accessibility: {
		id: string;
	};
	refs: {
		anchor: React.RefObject<HTMLButtonElement>;
		container: React.RefObject<HTMLDivElement>;
	};
	state: {
		open: boolean;
	};
	hooks: {
		onClose: () => void;
	};
}

export const DetectionOverlaySettings: React.FC<DetectionSettingsProps> = ({ accessibility, refs, state, hooks }) => {
	const { t } = useTranslation();
	const [faceDetectionEnabled, setFaceDetectionEnabled] = useAtom(faceDetectionEnabledAtom);
	const [objectDetectionEnabled, setObjectDetectionEnabled] = useAtom(objectDetectionEnabledAtom);
	const [gestureDetectionEnabled, setGestureDetectionEnabled] = useAtom(gestureDetectionEnabledAtom);
	const [gazeDirectionEnabled, setGazeDirectionEnabled] = useAtom(gazeDirectionEnabledAtom);
	const faceDetectionCapability = useAtomValue(faceDetectionCapabilityAtom);
	const objectDetectionCapability = useAtomValue(objectDetectionCapabilityAtom);

	const faceDetectionDisabled = !faceDetectionCapability;
	const objectDetectionDisabled = !objectDetectionCapability;
	const gestureDetectionDisabled = !faceDetectionCapability || !faceDetectionEnabled;
	const gazeDirectionDisabled = !faceDetectionCapability || !faceDetectionEnabled;

	if (!refs.anchor.current) {
		return null;
	}

	return (
		<Popover
			id={accessibility.id}
			open={state.open}
			anchorEl={refs.anchor.current}
			onClose={hooks.onClose}
			container={refs.container.current}
			anchorOrigin={{
				vertical: "bottom",
				horizontal: "center",
			}}
			transformOrigin={{
				vertical: "bottom",
				horizontal: "center",
			}}
			PaperProps={{
				sx: {
					backgroundColor: "transparent",
					boxShadow: "none",
					overflow: "visible",
					marginTop: "-60px",
				},
			}}
		>
			<div id="settingsPopover" className={styles.popoverContainer}>
				<div className={styles.settingsContainer}>
					<div className={styles.settingCategoryRow}>
						<Typography className={styles.labelText}>
							{t("result.components.studentSelection.videoPlayer.settings.aiCategory")}
						</Typography>
					</div>
					<div className={styles.settingActionableRow}>
						<Typography
							component="label"
							className={styles.settingActionableRowText}
							aria-disabled={faceDetectionDisabled}
						>
							{t("result.components.studentSelection.videoPlayer.settings.faceDetectionSubcategory")}
						</Typography>
						<ToggleButton
							hooks={{
								onChange: (checked) => {
									setFaceDetectionEnabled(checked);
									if (!checked) {
										setGestureDetectionEnabled(false);
										setGazeDirectionEnabled(false);
									}
								},
							}}
							state={{
								checked: faceDetectionEnabled,
								disabled: faceDetectionDisabled,
							}}
						/>
					</div>

					<div className={styles.settingActionableRow}>
						<Typography
							component="label"
							className={styles.settingActionableRowText}
							aria-disabled={gestureDetectionDisabled}
						>
							{t("result.components.studentSelection.videoPlayer.settings.gestureDetectionSubcategory")}
						</Typography>
						<ToggleButton
							hooks={{
								onChange: (checked) => {
									setGestureDetectionEnabled(checked);
								},
							}}
							state={{
								checked: gestureDetectionEnabled,
								disabled: gestureDetectionDisabled,
							}}
						/>
					</div>

					<div className={styles.settingActionableRow}>
						<Typography
							component="label"
							className={styles.settingActionableRowText}
							aria-disabled={gazeDirectionDisabled}
						>
							{t("result.components.studentSelection.videoPlayer.settings.gazeDirectionSubcategory")}
						</Typography>
						<ToggleButton
							hooks={{
								onChange: (checked) => {
									setGazeDirectionEnabled(checked);
								},
							}}
							state={{
								checked: gazeDirectionEnabled,
								disabled: gazeDirectionDisabled,
							}}
						/>
					</div>

					<div className={styles.settingActionableRow}>
						<Typography
							component="label"
							className={styles.settingActionableRowText}
							aria-disabled={objectDetectionDisabled}
						>
							{t("result.components.studentSelection.videoPlayer.settings.objectDetectionSubcategory")}
						</Typography>
						<ToggleButton
							hooks={{
								onChange: (checked) => {
									setObjectDetectionEnabled(checked);
								},
							}}
							state={{
								checked: objectDetectionEnabled,
								disabled: objectDetectionDisabled,
							}}
						/>
					</div>
				</div>
			</div>
		</Popover>
	);
};

export default DetectionOverlaySettings;
